import PropsType from 'prop-types'
import { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { useLocation } from 'react-router'
import { Panel } from '../../../../../common/panel'
import EventEmitter, {
  DID_MOUNT_COMPONENT,
} from '../../../../../helpers/EventEmitter'
import {
  formatCurrencyFollowLocale,
  formatText,
} from '../../../../../helpers/value'
import { CURRENCY } from '../../../../../utils/currency'
import { getOrderType } from '../../../common/helper'
import { PopupPaymentInformation } from '../../../common/popupPaymentInformation'
import {
  PAYMENT_METHOD,
  PAYMENT_METHOD_FREE,
  PAYMENT_METHOD_PACKAGE,
} from '../../constants'
import { DateOrderedCell } from '../../table/DateOrderedCell'
import { OrderStatusCell } from '../../table/OrderStatusCell'
import { PaymentStatusCell } from '../../table/PaymentStatusCell'
import { selectData } from '../store/slice'
import { Title } from './Title'
import { Volume } from './Volume'
import { VolumeTitle } from './VolumeTitle'

const leftSchema = {
  ORDER_NUMBER: {
    title: 'ORDER_NUMBER',
    key: 'id',
  },
  VOLUME: {
    title: 'VOLUME',
    key: 'quantity',
  },
  ORDER_STATUS: {
    title: 'ORDER_STATUS',
    key: 'status',
  },
  DATE_ORDERED: {
    title: 'DATE_ORDERED',
    key: 'createDate',
  },
  PAYMENT_METHOD: {
    title: 'PAYMENT_METHOD',
    key: 'paymentMethod',
  },
  PAYMENT_STATUS: {
    title: 'PAYMENT_STATUS',
    key: 'transactionStatus',
  },
}

const rightSchema = {
  TOTAL_PRICE: {
    title: 'TOTAL_PRICE',
    key: 'amountVND',
  },
  TOTAL_TRADE_DISCOUNT_ON_PRODUCT: {
    title: 'TOTAL_TRADE_DISCOUNT_ON_PRODUCT',
    key: 'totalDiscountValueOfDetailsVND',
  },
  TRADE_DISCOUNT_ON_ORDER: {
    title: 'TRADE_DISCOUNT_ON_ORDER',
    key: 'discountValueVND',
  },
  vatPrice: {
    title: 'VAT',
    key: 'vatVND',
  },
  TOTAL: {
    title: 'TOTAL',
    key: 'totalAmountVND',
  },
}

export const OrderInformation = ({ isPdf }) => {
  const location = useLocation()

  const { orderId, isScreening } = new Proxy(
    new URLSearchParams(location.search),
    {
      get: (searchParam, prop) => searchParam.get(prop),
    },
  )

  const data = useSelector(selectData)
  const locale = useSelector((state) => state.i18n.locale)

  const [isOpenPayNow, setIsOpenPayNow] = useState(false)

  const handlePayNow = () => {
    setIsOpenPayNow(true)
  }

  const renderContent = () => {
    return (
      <div className="dFlex p2">
        <div className="w50 dFlex">
          <div className="flexColumn gap2">
            {Object.values(leftSchema).map((item, index) => {
              if (item.key === leftSchema.VOLUME.key) {
                return (
                  <div key={index}>
                    <VolumeTitle />
                  </div>
                )
              }
              if (data[item.key] === PAYMENT_METHOD_FREE) {
                return <Fragment key={index}></Fragment>
              }
              return <Title key={index} title={item.title} />
            })}
          </div>
          <div className="flexColumn gap2 ml4">
            {Object.values(leftSchema).map((item, index) => {
              switch (item.key) {
                case leftSchema.VOLUME.key:
                  return (
                    <div key={index}>
                      <Volume />
                    </div>
                  )
                case leftSchema.ORDER_STATUS.key:
                  return (
                    <div key={index}>
                      <OrderStatusCell val={data[item.key]} />
                    </div>
                  )
                case leftSchema.DATE_ORDERED.key:
                  return (
                    <div key={index}>
                      <DateOrderedCell val={data[item.key]} locale={locale} />
                    </div>
                  )
                case leftSchema.PAYMENT_METHOD.key:
                  return (
                    <Fragment key={index}>
                      {data[item.key] &&
                      data[item.key] !== PAYMENT_METHOD_FREE ? (
                        <div>
                          <Translate value={PAYMENT_METHOD[data[item.key]]} />
                        </div>
                      ) : (
                        <></>
                      )}
                    </Fragment>
                  )
                case leftSchema.PAYMENT_STATUS.key:
                  return (
                    <div key={index}>
                      <PaymentStatusCell
                        val={data[item.key]}
                        orderStatus={data?.status}
                        handlePayNow={handlePayNow}
                      />
                    </div>
                  )
                default:
                  return <div key={index}>{formatText(data[item.key])}</div>
              }
            })}
          </div>
        </div>
        <div className="w50 dFlex">
          <div className="flexColumn gap2">
            {Object.values(rightSchema).map((item, index) => {
              return (
                <div key={index}>
                  <Title title={item.title} />
                </div>
              )
            })}
          </div>
          {data?.paymentMethod !== PAYMENT_METHOD_PACKAGE ? (
            <div className="flexColumn gap2 ml4">
              {Object.values(rightSchema).map((item, index) => {
                return (
                  <div key={index}>
                    {formatCurrencyFollowLocale(
                      data[item.key],
                      CURRENCY.VND,
                      locale,
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
    )
  }
  useEffect(() => {
    if (isPdf) {
      EventEmitter.dispatch(DID_MOUNT_COMPONENT)
    }
  }, [])

  return (
    <Panel
      label="userManagement.orderManagement.orderDetail.ORDER_INFORMATION"
      style={{ marginTop: 0 }}
      isI18n
    >
      {renderContent()}
      {isOpenPayNow && (
        <PopupPaymentInformation
          isShow={isOpenPayNow}
          setIsShow={setIsOpenPayNow}
          orderId={orderId}
          orderType={getOrderType(data)}
          isScreening={isScreening}
          oldPrice={data?.amount}
          discountedAmount={data?.amount - data?.totalDiscountValueOfDetails}
          discountValue={data?.discountValue}
          vatPrice={data?.vat}
          grandTotalPrice={data?.totalAmount}
          exchangeRateAPI={data?.exchangeRate}
        />
      )}
    </Panel>
  )
}

OrderInformation.propTypes = {
  isPdf: PropsType.bool,
}

OrderInformation.defaultProps = {
  isPdf: false,
}
